import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { posAPI, staffToken } from "../services/api";
import { useI18n } from "../i18n";
import { useShopEvent } from "../hooks/useShopEvent";
import { daysFromToday } from "../utils/dates";
import NewOrderAlert from "../components/NewOrderAlert";
import OwnerApproval from "./components/OwnerApproval";
import SupplierSheet from "./components/SupplierSheet";
import DoseSheet from "./components/DoseSheet";
import DrawerSheet from "./components/DrawerSheet";
import DrawerGate from "./components/DrawerGate";
import { useDrawer } from "./hooks/useDrawer";
import CartBar from "./components/CartBar";
import CategoryChips from "./components/CategoryChips";
import ConfirmDialog from "./components/ConfirmDialog";
import HeldTicketsSheet from "./components/HeldTicketsSheet";
import PosHeader from "./components/PosHeader";
import TillLock from "./components/TillLock";
import PickupsSheet from "./components/PickupsSheet";
import GroupSheet from "./components/GroupSheet";
import PackChoiceSheet from "./components/PackChoiceSheet";
import ProductGrid from "./components/ProductGrid";
import RecentSalesSheet from "./components/RecentSalesSheet";
import ReceiptOverlay from "./components/ReceiptOverlay";
import SearchBar from "./components/SearchBar";
import TicketPanel from "./components/TicketPanel";
import TicketSheet from "./components/TicketSheet";
import { useCatalog } from "./hooks/useCatalog";
import { useFrequentItems } from "./hooks/useFrequentItems";
import { useHeldTickets } from "./hooks/useHeldTickets";
import { usePosOutbox } from "./hooks/usePosOutbox";
import { useTicket } from "./hooks/useTicket";
import { setPullRefreshGuard } from "../utils/pullRefreshGuard";
import { categoryIcon, categoryLabel } from "./utils/categories";
import {
  buildGroupIndex,
  countTiles,
  countTilesBy,
  membersByGroup,
  toTiles,
} from "./utils/groups";
import { paymentState } from "./utils/payment";
import { toSalePayload } from "./utils/packs";
import { hasSubcategories, subcategoriesFor } from "../constants/subcategories";
import "./pos.css";

const EMPTY_SUMMARY = {
  sales_count: 0,
  revenue: 0,
  average_sale: 0,
  by_payment_method: {},
};

const CLOSED_SHEET = { open: false, focus: "top" };

// Same split as the product form: fruit/salt are rated on sweetness+coldness,
// gourmand (desserts, pastries, creams) on sweetness+hardness instead.
const JUICE_TYPES = ["fruit", "gourmand", "salt"];

// Scrolling starts clipping the filter rows after this many pixels, 1px of
// height for every 1px scrolled beyond it — see the effect that uses it for
// why (short version: it's what makes the collapse never fight the page's
// own scrollable height, which is what flickered before).
const FILTERS_COLLAPSE_START = 8;

// (The header itself is two rows on a phone — see PosHeader — and the search
// row below it is positioned from that: top-[5.75rem], i.e. h-12 + h-11.)
// The fixed header's own vertical padding (pt-3 + pb-2 below) — kept as a
// constant here too since `main`'s reserved top padding has to account for
// it, and reading it back from the (partly scroll-collapsed) DOM instead
// would fold the current scroll position into a number that's supposed to
// stay fixed at the header's fully-expanded size.
const HEADER_PADDING_Y = 20;

// On a phone the header's second row (the action buttons) slides away once the
// list is scrolled past ACTIONS_HIDE_AFTER, and returns on any scroll up. A
// movement smaller than ACTIONS_SCROLL_STEP is ignored, so a thumb resting on
// the glass or iOS's rubber-band at either end does not make it flicker.
const ACTIONS_HIDE_AFTER = 24;
const ACTIONS_SCROLL_STEP = 8;

/** How often an unattended till checks whether its cashier's shift ran out. */
const SHIFT_CHECK_MS = 60000;

/**
 * How often the pickups badge is re-read without being told. The live
 * connection normally keeps it current; this catches midnight, when
 * "tomorrow" silently becomes "today".
 */
const PICKUPS_REFRESH_MS = 5 * 60000;

/** Identifies a basket across retries so a replayed sale is never charged twice. */
const newClientSaleId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();

  return `pos_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const matchesSearch = (product, query) =>
  [
    product.name,
    product.brand,
    product.subcategory,
    product.category,
    product.search_terms,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .includes(query);

/**
 * The in-store register.
 *
 * Optimised for a queue of waiting customers: no password — a four-digit PIN
 * says who is serving — the whole catalog one tap away, payment two taps from
 * a full basket, and tickets that can be parked or replayed later if the
 * connection drops.
 */
const PosRegister = () => {
  const { t, lang } = useI18n();
  const catalog = useCatalog();
  const ticket = useTicket();
  const held = useHeldTickets();
  const outbox = usePosOutbox();

  // Pulling down to refresh must not silently wipe a ticket being rung up
  const ticketOpen = !ticket.isEmpty;
  useEffect(() => setPullRefreshGuard(() => ticketOpen), [ticketOpen]);
  const frequent = useFrequentItems();

  const [search, setSearch] = useState("");
  const [activeChip, setActiveChip] = useState("all");
  const [activeSubcategory, setActiveSubcategory] = useState("all");
  const [activeJuiceType, setActiveJuiceType] = useState("all");
  const [activeBrand, setActiveBrand] = useState("all");
  const [openGroupId, setOpenGroupId] = useState(null);
  // A pack with a range in it, waiting for the cashier to fill its choices
  const [packChoice, setPackChoice] = useState(null);
  const [ticketSheet, setTicketSheet] = useState(CLOSED_SHEET);
  const [heldOpen, setHeldOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState("");
  // A sale refused as below cost, waiting for an owner: { payload, products }
  const [approval, setApproval] = useState(null);

  const [method, setMethod] = useState("cash");
  const [tendered, setTendered] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [recentSales, setRecentSales] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Who is serving. Required: the till stays locked until a cashier enters
  // their PIN, so every sale is attributed to someone. The token survives a
  // closed tab or browser for its own 12h lifetime (see staffToken), so it
  // is not re-asked for on every fresh start of the app — only once the
  // shift token expires, someone signs out, or the till is locked by hand.
  const [staff, setStaff] = useState(() => staffToken.staff());
  const locked = !staff;
  // The shop may have switched credit or pre-orders off; the server refuses
  // them too, this only stops the till from offering what will not work
  const modulesOn = catalog.store?.enabled_modules || {};
  const preordersOn = modulesOn.preorders !== false;
  const canExtendCredit =
    staffToken.staffCan(staff, "power.extend_credit") && modulesOn.credit_sales !== false;
  const canReceive = staffToken.staffCan(staff, "deliveries.edit");
  const canPayOut = staffToken.staffCan(staff, "power.manage_cash");
  // Re-read whenever the cashier changes: what they may see differs
  const drawer = useDrawer(staff?.member_id);
  const { refresh: refreshDrawer } = drawer;

  // Who is buying, when their card was scanned. Optional, and part of the
  // ticket: it is offered once there is a basket and leaves with it.
  const [customer, setCustomer] = useState(null);
  // Whose tab a credit sale goes on when no card is attached: { id?, name }
  const [creditTab, setCreditTab] = useState(null);
  const [pickupsOpen, setPickupsOpen] = useState(false);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [dosesOpen, setDosesOpen] = useState(false);
  // Opened from the drawer's "Pay a supplier": straight to paying
  const [supplierStart, setSupplierStart] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [notice, setNotice] = useState("");

  // Orders waiting at the counter, for the badge on 📦
  const [pickups, setPickups] = useState([]);

  const searchRef = useRef(null);
  const mainRef = useRef(null);
  const searchRowRef = useRef(null);
  const filtersClipRef = useRef(null);
  const filtersInnerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [actionsHidden, setActionsHidden] = useState(false);
  const [headerRect, setHeaderRect] = useState({ left: 0, width: 0 });
  const { currency, low_stock_threshold: lowStockThreshold } = catalog.store;

  const lock = useCallback(() => {
    staffToken.clear();
    setStaff(null);

    // The next cashier starts on the register itself. The ticket stays, so a
    // hand-over mid-sale does not lose the customer's basket.
    setTicketSheet(CLOSED_SHEET);
    setHeldOpen(false);
    setOpenGroupId(null);
    setPackChoice(null);
    setHistoryOpen(false);
    setPickupsOpen(false);
    setSupplierOpen(false);
    setDosesOpen(false);
    setDrawerOpen(false);
    setApproval(null);
  }, []);

  // The server stops naming the cashier when their shift token expires; the
  // till locks at the same moment rather than selling on as nobody
  useEffect(() => {
    if (!staff) return undefined;

    const check = () => {
      if (staffToken.isExpired()) lock();
    };

    check();
    const interval = setInterval(check, SHIFT_CHECK_MS);
    document.addEventListener("visibilitychange", check);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", check);
    };
  }, [staff, lock]);

  // What this cashier may do is read again from the server: the list in
  // storage is from the moment of the PIN, and the owner may tick (or untick)
  // something afterwards. A member removed since is locked out here too.
  const staffMemberId = staff?.member_id;
  useEffect(() => {
    if (!staffMemberId) return undefined;

    let cancelled = false;
    const sync = async () => {
      try {
        const { data } = await posAPI.me();
        if (cancelled) return;

        if (!data.staff) {
          lock();
          return;
        }

        setStaff((current) => {
          if (!current || JSON.stringify(current.permissions) === JSON.stringify(data.staff.permissions)) {
            return current;
          }
          const next = { ...current, permissions: data.staff.permissions, role: data.staff.role };
          staffToken.set(staffToken.get(), next);
          return next;
        });
      } catch {
        // Offline: keep what the till knew; the server still checks every call
      }
    };

    sync();
    const interval = setInterval(sync, SHIFT_CHECK_MS);
    const onVisible = () => document.visibilityState === "visible" && sync();
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [staffMemberId, lock]);

  // A card belongs to a basket: once the basket is gone, so is the card, or
  // the next customer's purchase would be credited to this one
  useEffect(() => {
    if (ticket.isEmpty) {
      setCustomer(null);
      setCreditTab(null);
    }
  }, [ticket.isEmpty]);

  const loadPickups = useCallback(async () => {
    if (!preordersOn) {
      setPickups([]);
      return;
    }

    try {
      const { data } = await posAPI.pendingPickups();
      setPickups(data);
    } catch {
      // The badge is a prompt, not a record; keep the last known count
    }
  }, [preordersOn]);

  // Read on open, whenever the shop's live connection says an order was
  // placed or moved, after every reconnect (anything missed while offline),
  // when the app comes back to the foreground, and now and then regardless
  useEffect(() => {
    loadPickups();

    const interval = setInterval(loadPickups, PICKUPS_REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") loadPickups();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [loadPickups]);

  useShopEvent("preorder:created", loadPickups);
  useShopEvent("preorder:updated", loadPickups);
  useShopEvent("connect", loadPickups);

  // Due today, or already late: what someone may walk in for right now
  const pickupsDue = useMemo(
    () =>
      pickups.filter((order) => {
        const diff = daysFromToday(order.pickup_date);
        return diff !== null && diff <= 0;
      }).length,
    [pickups],
  );

  const refreshSummary = useCallback(async () => {
    try {
      const response = await posAPI.getTodaySummary();
      setSummary(response.data);
    } catch (err) {
      // The takings strip is informational; a failure must not block selling.
    }
  }, []);

  useEffect(() => {
    refreshSummary();
  }, [refreshSummary]);

  // Queued sales that reach the server later still have to show up in the totals
  const { onSynced } = outbox;
  const { refresh: refreshCatalog } = catalog;

  useEffect(() => {
    onSynced(() => {
      refreshSummary();
      refreshCatalog({ silent: true });
    });
  }, [onSynced, refreshSummary, refreshCatalog]);

  // The header is `fixed`, not `sticky`: it overlays the page rather than
  // occupying flow space, with `main` given a top padding equal to its own
  // full (uncollapsed) height instead. That's what makes the collapse below
  // purely cosmetic to the header's own box — shrinking it can never change
  // the page's scrollable height, which is what made a scrollY-driven
  // collapse flicker before: on a short product list, collapsing the (then
  // in-flow, sticky) header could itself make the page no longer tall enough
  // to scroll, snapping scrollY back to 0, re-expanding the header, and
  // repeating. With the header out of flow that feedback loop can't happen,
  // at any list length. Fixed positioning is relative to the viewport rather
  // than `main`'s own flex column though, so its left/width are tracked
  // separately — the register sits beside the ticket panel on wider screens.
  useEffect(() => {
    const mainEl = mainRef.current;
    if (!mainEl) return undefined;

    const update = () => {
      const rect = mainEl.getBoundingClientRect();
      setHeaderRect({ left: rect.left, width: rect.width });
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(mainEl);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
    };
    // catalog.loading: `main` doesn't exist yet on the loading screen, so
    // mainRef.current is still null the first time this runs — rerunning
    // once loading flips to false is what actually attaches the observer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog.loading]);

  // Tracks the filter rows' own natural height — it changes whenever those
  // rows do (a category with a subcategory or juice-type row is taller than
  // one without), not just on scroll — and keeps `main`'s reserved top
  // padding in sync so nothing is hidden under the header nor left with a
  // gap below it.
  useEffect(() => {
    const searchEl = searchRowRef.current;
    const filtersEl = filtersInnerRef.current;
    if (!searchEl || !filtersEl) return undefined;

    const update = () => {
      setHeaderHeight(searchEl.offsetHeight + filtersEl.offsetHeight);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(searchEl);
    observer.observe(filtersEl);
    return () => observer.disconnect();
    // catalog.loading: same reason as the effect above — these refs are
    // null until the loading screen gives way to the real layout.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog.loading]);

  // The actual collapse: 1px of the filter rows' height clipped for every
  // 1px scrolled past FILTERS_COLLAPSE_START, driving the clip wrapper's
  // height directly (no React state, no CSS transition) so it tracks the
  // scroll position exactly rather than animating toward a stale target.
  useLayoutEffect(() => {
    const clipEl = filtersClipRef.current;
    const innerEl = filtersInnerRef.current;
    if (!clipEl || !innerEl) return undefined;

    let frame = null;

    const evaluate = () => {
      frame = null;
      const natural = innerEl.offsetHeight;

      // The two-column layout stretches `main` to match the ticket panel's
      // height (flex's default cross-axis stretch), so a short product list
      // (2-3 tiles) can still leave the page scrollable by a few pixels when
      // the ticket panel is the taller column — and `main`'s own rendered box
      // is stretched right along with it, so measuring `main` itself would
      // say it "needs" that scroll too. The product grid is `main`'s last
      // child and, unlike `main`, is sized by its own content rather than by
      // flex stretch, so its bottom edge is what actually says whether there
      // is more of the grid to reveal. Adding scrollY back to the
      // viewport-relative rect cancels out the current scroll position, so
      // this reads the same regardless of where the page happens to be
      // scrolled to right now.
      const contentEl = mainRef.current?.lastElementChild;
      const mainFits =
        contentEl != null &&
        contentEl.getBoundingClientRect().bottom + window.scrollY <=
          window.innerHeight;

      const collapsed = mainFits
        ? 0
        : Math.min(
            Math.max(window.scrollY - FILTERS_COLLAPSE_START, 0),
            natural,
          );
      clipEl.style.height = `${natural - collapsed}px`;
    };

    const onScroll = () => {
      if (frame === null) frame = requestAnimationFrame(evaluate);
    };

    evaluate();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [headerHeight]);

  // Hide the header's action row while scrolling down, show it on any scroll
  // up (phones only: from `sm` up the header is one row and never moves).
  // Purely a transform/opacity change on the header and the search row — the
  // page's scrollable height is untouched, so this cannot feed back into the
  // scroll position the way the filter collapse once did.
  useEffect(() => {
    const phone = window.matchMedia("(max-width: 639px)");
    let anchor = window.scrollY;
    let frame = null;

    const evaluate = () => {
      frame = null;
      const y = Math.max(window.scrollY, 0);

      if (!phone.matches || y <= ACTIONS_HIDE_AFTER) {
        setActionsHidden(false);
        anchor = y;
        return;
      }

      const moved = y - anchor;
      if (moved > ACTIONS_SCROLL_STEP) {
        setActionsHidden(true);
        anchor = y;
      } else if (moved < -ACTIONS_SCROLL_STEP) {
        setActionsHidden(false);
        anchor = y;
      }
    };

    const onScroll = () => {
      if (frame === null) frame = requestAnimationFrame(evaluate);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  // A range is one tile however many flavours it has, so every chip counts
  // tiles, not products — "Puff 3" should match the three tiles it shows.
  const groupIndex = useMemo(
    () => buildGroupIndex(catalog.groups),
    [catalog.groups],
  );
  const membersOf = useMemo(
    () => membersByGroup(catalog.products),
    [catalog.products],
  );

  const chips = useMemo(() => {
    const counts = countTilesBy(
      catalog.products,
      (product) => product.category,
      groupIndex,
    );

    const categoryChips = Object.entries(counts)
      .sort(([, a], [, b]) => b - a)
      .map(([category, count]) => ({
        id: category,
        label: categoryLabel(category),
        icon: categoryIcon(category),
        count,
      }));

    const topCount = frequent.topProducts(catalog.products).length;

    return [
      {
        id: "all",
        label: t("pos.chips.all"),
        count: countTiles(catalog.products, groupIndex),
      },
      ...(topCount > 0
        ? [{ id: "top", label: t("pos.chips.top"), icon: "⭐", count: topCount }]
        : []),
      ...categoryChips,
    ];
    // lang: the labels are words, so they are rebuilt when the language changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog.products, groupIndex, frequent, lang]);

  // Only offered once a category is picked — "all" and "top" mix categories,
  // where a subcategory or juice type wouldn't mean anything.
  const categoryProducts = useMemo(
    () => catalog.products.filter((product) => product.category === activeChip),
    [catalog.products, activeChip],
  );

  // Dynamic, like the top-level category chips: a shop with no salt juices
  // yet shouldn't show a "Salt" chip that always comes up empty.
  const subcategoryChips = useMemo(() => {
    if (!hasSubcategories(activeChip)) return [];

    const counts = countTilesBy(
      categoryProducts,
      (product) => product.subcategory,
      groupIndex,
    );

    const options = subcategoriesFor(activeChip).filter(({ value }) => counts[value]);
    if (options.length === 0) return [];

    return [
      {
        id: "all",
        label: t("pos.chips.all"),
        count: countTiles(categoryProducts, groupIndex),
      },
      ...options.map(({ value, label }) => ({ id: value, label, count: counts[value] })),
    ];
    // lang: subcategoryLabel() reads the current language directly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryProducts, groupIndex, activeChip, t, lang]);

  const juiceTypeChips = useMemo(() => {
    if (activeChip !== "juice") return [];

    const counts = countTilesBy(
      categoryProducts,
      (product) => product.juice_details?.juice_type,
      groupIndex,
    );

    const present = JUICE_TYPES.filter((type) => counts[type]);
    if (present.length === 0) return [];

    return [
      {
        id: "all",
        label: t("pos.chips.all"),
        count: countTiles(categoryProducts, groupIndex),
      },
      ...present.map((type) => ({
        id: type,
        label: t(`products.form.juiceTypes.${type}`),
        count: counts[type],
      })),
    ];
  }, [categoryProducts, groupIndex, activeChip, t]);

  // Brand is orthogonal to category — useful even browsing "All" or "Top
  // sellers" — so it's scoped to whatever subcategory/juice-type is already
  // narrowing the view rather than gated behind picking a category first.
  const brandBase = useMemo(() => {
    const source =
      activeChip === "all" || activeChip === "top" ? catalog.products : categoryProducts;

    return source.filter((product) => {
      if (activeSubcategory !== "all" && product.subcategory !== activeSubcategory) {
        return false;
      }
      if (
        activeJuiceType !== "all" &&
        product.juice_details?.juice_type !== activeJuiceType
      ) {
        return false;
      }
      return true;
    });
  }, [catalog.products, categoryProducts, activeChip, activeSubcategory, activeJuiceType]);

  const brandChips = useMemo(() => {
    const counts = countTilesBy(brandBase, (product) => product.brand, groupIndex);

    const brands = Object.keys(counts).sort((a, b) => a.localeCompare(b));
    // Filtering to the only brand present would do nothing — same guard as
    // the customer menu's own brand picker.
    if (brands.length < 2) return [];

    return [
      {
        id: "all",
        label: t("pos.chips.all"),
        count: countTiles(brandBase, groupIndex),
      },
      ...brands.map((brand) => ({ id: brand, label: brand, count: counts[brand] })),
    ];
  }, [brandBase, groupIndex, t]);

  const visibleProducts = useMemo(() => {
    const query = search.trim().toLowerCase();
    let list;

    // A search always looks at the whole catalog: hunting for a product is not
    // the moment to remember which chip is selected.
    if (query) {
      list = catalog.products.filter((product) => matchesSearch(product, query));
    } else if (activeChip === "top") {
      list = frequent.topProducts(catalog.products);
    } else if (activeChip === "all") {
      list = catalog.products;
    } else {
      list = categoryProducts;

      if (activeSubcategory !== "all") {
        list = list.filter((product) => product.subcategory === activeSubcategory);
      }

      if (activeJuiceType !== "all") {
        list = list.filter(
          (product) => product.juice_details?.juice_type === activeJuiceType,
        );
      }
    }

    if (!query && activeBrand !== "all") {
      list = list.filter((product) => product.brand === activeBrand);
    }

    // Out-of-stock items sort last rather than being interspersed, same as
    // the customer menu — a cashier scanning the grid shouldn't have to read
    // past a "Out" tile between two things actually on the shelf.
    return [...list].sort(
      (a, b) => (a.stock_quantity <= 0) - (b.stock_quantity <= 0),
    );
  }, [
    catalog.products,
    categoryProducts,
    search,
    activeChip,
    activeSubcategory,
    activeJuiceType,
    activeBrand,
    frequent,
  ]);

  // Ranges stay collapsed while browsing, so a long line of flavours does not
  // bury everything else. A search or the top sellers are hunting for one
  // specific product, so they show it directly, one tap from the ticket.
  const tiles = useMemo(
    () =>
      toTiles(visibleProducts, groupIndex, membersOf, {
        collapse: !search.trim() && activeChip !== "top",
      }),
    [visibleProducts, groupIndex, membersOf, search, activeChip],
  );

  const openGroup = openGroupId ? groupIndex.get(openGroupId) : null;

  const addToTicket = useCallback(
    (product) => {
      ticket.addProduct(product);
      if (navigator.vibrate) navigator.vibrate(8);
    },
    [ticket],
  );

  const handleAdd = useCallback(
    (product) => {
      // A pack with a range in it is added once its choices are made
      if (product.needs_choice) {
        setPackChoice(product);
        return;
      }

      // Selling an item the system believes is out of stock is allowed — shop
      // counts drift — but never silently.
      if (product.stock_quantity <= 0 && ticket.quantityOf(product.id) === 0) {
        setConfirm({
          type: "oversell",
          product,
          title: t("pos.oversell.title"),
          message: t("pos.oversell.message", { name: product.name }),
          confirmLabel: t("pos.oversell.confirm"),
        });
        return;
      }

      addToTicket(product);
    },
    [addToTicket, ticket, t],
  );

  const handleSearchSubmit = useCallback(() => {
    if (visibleProducts.length === 0) return;

    handleAdd(visibleProducts[0]);
    setSearch("");
  }, [visibleProducts, handleAdd]);

  const resetPayment = useCallback(() => {
    setMethod("cash");
    setTendered("");
    setCreditTab(null);
  }, []);

  const holdTicket = useCallback(() => {
    held.hold({ ...ticket, customer });
    ticket.reset();
    setCustomer(null);
    resetPayment();
    setTicketSheet(CLOSED_SHEET);
  }, [held, ticket, customer, resetPayment]);

  const resumeTicket = useCallback(
    (id) => {
      if (!ticket.isEmpty) held.hold({ ...ticket, customer });

      const restored = held.release(id);
      if (restored) {
        ticket.restore(restored);
        setCustomer(restored.customer || null);
      }

      resetPayment();
      setHeldOpen(false);
    },
    [held, ticket, customer, resetPayment],
  );

  const openHistory = useCallback(async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    refreshSummary();

    try {
      const response = await posAPI.getRecentSales(20);
      setRecentSales(response.data);
    } catch (err) {
      setRecentSales([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [refreshSummary]);

  /**
   * Sends a sale as built by completeSale. Kept apart so an owner's approval
   * can send the very same sale again — same client_sale_id, which is what
   * the approval is bound to — with its override token.
   */
  const submitSale = useCallback(
    async (payload) => {
      const { totals, lines } = ticket;
      const { amountPaid, changeDue } = paymentState(
        method,
        tendered,
        totals.total,
      );

      setSubmitting(true);
      setError("");

      try {
        const result = await outbox.submit(payload);
        const sale = result.sale;

        setReceipt({
          receiptNumber: sale?.receipt_number || null,
          queued: result.status === "queued",
          lines,
          subtotal: totals.subtotal,
          discountAmount: totals.discountAmount,
          total: totals.total,
          paymentMethod: method,
          amountPaid,
          changeDue,
          soldAt: sale?.created_at || new Date().toISOString(),
        });

        frequent.recordSale(lines);
        ticket.reset();
        resetPayment();
        // The next person in the queue is not this one — a card left attached
        // would quietly credit their purchase to whoever came before.
        setCustomer(null);
        setTicketSheet(CLOSED_SHEET);
        setSearch("");

        if (result.status === "recorded") {
          refreshSummary();
          refreshCatalog({ silent: true });
          refreshDrawer();
        }
      } catch (err) {
        // The discount takes something below cost: an owner can approve it
        // Closed on another till, or at the end of the day: count and open it
        if (err.response?.data?.code === "DRAWER_CLOSED") {
          refreshDrawer();
          return;
        }

        if (err.response?.data?.code === "BELOW_COST") {
          setApproval({
            payload,
            products: err.response.data.details?.products || [],
          });
          return;
        }

        setError(
          err.response?.data?.message ||
            err.response?.data?.error ||
            t("pos.saleFailed"),
        );
      } finally {
        setSubmitting(false);
      }
    },
    [
      ticket,
      tendered,
      method,
      outbox,
      frequent,
      resetPayment,
      refreshSummary,
      refreshCatalog,
      refreshDrawer,
      t,
    ],
  );

  const completeSale = useCallback(() => {
    if (ticket.isEmpty || submitting) return;

    const { totals, lines, discount } = ticket;
    const { amountPaid } = paymentState(method, tendered, totals.total);

    submitSale({
      client_sale_id: newClientSaleId(),
      ...toSalePayload(lines),
      payment_method: method,
      customer_id: customer?.id,
      // Only when no card says whose tab it is: a picked tab, or a name
      ...(method === "credit" && !customer && creditTab
        ? creditTab.id
          ? { credit_account_id: creditTab.id }
          : { credit_name: creditTab.name.trim() }
        : {}),
      discount: discount.value ? discount : undefined,
      amount_paid:
        method === "cash" || method === "credit" ? amountPaid : undefined,
      customer_label: ticket.customerLabel || undefined,
      allow_oversell: lines.some(
        (line) => line.quantity > line.product.stock_quantity,
      ),
    });
  }, [ticket, submitting, tendered, method, customer, creditTab, submitSale]);

  // Keyboard shortcuts for a counter with a keyboard or a barcode wedge
  useEffect(() => {
    const onKeyDown = (event) => {
      if (
        event.key === "F2" ||
        (event.key === "/" && event.target === document.body)
      ) {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // A typed amount belongs to the method it was typed under — switching
  // from cash to credit must not hand over a stale tendered figure as the
  // upfront payment.
  const handleMethodChange = useCallback((nextMethod) => {
    setMethod(nextMethod);
    setTendered("");
  }, []);

  const payment = {
    method,
    onMethodChange: handleMethodChange,
    tendered,
    onTenderedChange: setTendered,
    submitting,
    onComplete: completeSale,
    creditTab,
    onCreditTabChange: setCreditTab,
  };

  const askToClear = () =>
    setConfirm({
      type: "clear",
      title: t("pos.clear.title"),
      message: t("pos.clear.message"),
      confirmLabel: t("pos.clear.confirm"),
      tone: "danger",
    });

  // Covers everything, including the loading state: nothing about the till
  // is usable, or even visible, until someone has said who they are
  const tillLock = locked && (
    <TillLock storeName={catalog.store?.name} onUnlocked={setStaff} />
  );

  // Announced even while locked; opening the orders waits for a PIN
  const orderAlert = (
    <NewOrderAlert
      currency={currency}
      canView={!locked}
      onView={() => setPickupsOpen(true)}
    />
  );

  if (catalog.loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-14 w-14 border-t-4 border-primary-500" />
        <p className="text-slate-400 text-sm">{t("pos.loading")}</p>
        {tillLock}
        {orderAlert}
      </div>
    );
  }

  return (
    <>
      {/* inert while locked: nothing behind the lock can be tapped or focused */}
      <div className="min-h-screen bg-slate-950 text-slate-100" inert={locked}>
        <PosHeader
          storeName={catalog.store.name}
          cashierName={staff?.name?.split(" ")[0]}
          currency={currency}
          todayRevenue={summary.revenue}
          todaySales={summary.sales_count}
          heldCount={held.count}
          unsyncedCount={outbox.pendingCount + outbox.failed.length}
          pickupsDue={pickupsDue}
          actionsHidden={actionsHidden}
          onRevealActions={() => setActionsHidden(false)}
          onOpenHeld={() => setHeldOpen(true)}
          onOpenHistory={openHistory}
          onOpenPickups={preordersOn ? () => setPickupsOpen(true) : undefined}
          onOpenDrawer={drawer.enabled && drawer.open ? () => setDrawerOpen(true) : undefined}
          drawerAmount={drawer.expected}
          onOpenSupplier={
            catalog.store.enabled_modules?.suppliers !== false && (canReceive || canPayOut)
              ? () => setSupplierOpen(true)
              : undefined
          }
          onOpenDoses={
            catalog.store?.flags?.dose_sales === true && catalog.products.some((product) => product.sold_by_dose)
              ? () => setDosesOpen(true)
              : undefined
          }
          onLock={lock}
        />

        <div className="flex">
          <main
            ref={mainRef}
            className="flex-1 min-w-0 px-3 sm:px-4 pb-28 lg:pb-6"
            style={{ paddingTop: headerHeight + HEADER_PADDING_Y }}
          >
            <div
              className={`fixed z-20 px-3 sm:px-4 pt-3 pb-2 bg-slate-950/95 backdrop-blur top-[5.75rem] sm:top-14 transition-transform duration-200 ease-out ${
                // Follows the header's action row (h-11) as it slides away
                actionsHidden ? "-translate-y-11 sm:translate-y-0" : ""
              }`}
              style={{ left: headerRect.left, width: headerRect.width }}
            >
              <div ref={searchRowRef}>
                <SearchBar
                  value={search}
                  onChange={setSearch}
                  onSubmit={handleSearchSubmit}
                  inputRef={searchRef}
                  resultCount={visibleProducts.length}
                />
              </div>

              <div ref={filtersClipRef} className="overflow-hidden">
                <div ref={filtersInnerRef} className="pt-2 space-y-2">
                  <CategoryChips
                    chips={chips}
                    activeChip={search ? "all" : activeChip}
                    onSelect={(chip) => {
                      setSearch("");
                      setActiveChip(chip);
                      setActiveSubcategory("all");
                      setActiveJuiceType("all");
                      setActiveBrand("all");
                    }}
                  />

                  {!search && subcategoryChips.length > 0 && (
                    <CategoryChips
                      chips={subcategoryChips}
                      activeChip={activeSubcategory}
                      onSelect={(value) => {
                        setActiveSubcategory(value);
                        setActiveBrand("all");
                      }}
                    />
                  )}

                  {!search && juiceTypeChips.length > 0 && (
                    <CategoryChips
                      chips={juiceTypeChips}
                      activeChip={activeJuiceType}
                      onSelect={(value) => {
                        setActiveJuiceType(value);
                        setActiveBrand("all");
                      }}
                    />
                  )}

                  {!search && brandChips.length > 0 && (
                    <CategoryChips
                      chips={brandChips}
                      activeChip={activeBrand}
                      onSelect={setActiveBrand}
                    />
                  )}
                </div>
              </div>
            </div>

            {(error || catalog.error) && (
              <div className="mb-3 flex items-start gap-3 rounded-xl bg-red-500/15 border border-red-500/40 px-4 py-3">
                <span className="text-lg leading-none">⚠️</span>
                <p className="flex-1 text-sm font-semibold text-red-200">
                  {error || catalog.error}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setError("");
                    if (catalog.error) catalog.refresh();
                  }}
                  className="text-xs font-bold text-red-200 underline"
                >
                  {catalog.error ? t("common.retry") : t("common.dismiss")}
                </button>
              </div>
            )}

            {notice && (
              <button
                type="button"
                onClick={() => setNotice("")}
                className="mb-3 w-full text-start rounded-xl bg-emerald-500/15 border border-emerald-500/40 px-4 py-2.5 text-sm font-semibold text-emerald-200"
              >
                ✓ {notice}
              </button>
            )}

            {outbox.pendingCount > 0 && (
              <button
                type="button"
                onClick={openHistory}
                className="mb-3 w-full text-start rounded-xl bg-amber-500/15 border border-amber-500/40 px-4 py-2.5 text-sm font-semibold text-amber-200"
              >
                {t("pos.offlineQueued", { count: outbox.pendingCount })}
              </button>
            )}

            <ProductGrid
              tiles={tiles}
              quantityOf={ticket.quantityOf}
              lowStockThreshold={lowStockThreshold}
              onAdd={handleAdd}
              onOpenGroup={(group) => setOpenGroupId(group.id)}
              search={search}
              shelfEmpty={catalog.products.length === 0 && catalog.store?.flags?.till_empty_shelf === true}
            />
          </main>

          <TicketPanel
            ticket={ticket}
            currency={currency}
            payment={payment}
            customer={customer}
            onCustomerPicked={setCustomer}
            onCustomerCleared={() => setCustomer(null)}
            canExtendCredit={canExtendCredit}
            onHold={holdTicket}
            onClear={askToClear}
          />
        </div>

        <CartBar
          totals={ticket.totals}
          currency={currency}
          onReview={() => setTicketSheet({ open: true, focus: "top" })}
          onCharge={() => setTicketSheet({ open: true, focus: "pay" })}
        />

        <ConfirmDialog
          open={Boolean(confirm)}
          title={confirm?.title}
          message={confirm?.message}
          confirmLabel={confirm?.confirmLabel}
          tone={confirm?.tone}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            if (confirm.type === "oversell") addToTicket(confirm.product);
            if (confirm.type === "clear") {
              ticket.reset();
              setCustomer(null);
              resetPayment();
              setTicketSheet(CLOSED_SHEET);
            }
            setConfirm(null);
          }}
        />

        {receipt && (
          <ReceiptOverlay
            receipt={receipt}
            storeName={catalog.store.name}
            currency={currency}
            onNewSale={() => setReceipt(null)}
          />
        )}
      </div>

      {/* Sheets render into document.body, so they sit outside the inert
          wrapper — each one is closed by lock() before the till locks */}
      <TicketSheet
        open={ticketSheet.open && !locked}
        focus={ticketSheet.focus}
        onClose={() => setTicketSheet(CLOSED_SHEET)}
        ticket={ticket}
        currency={currency}
        payment={payment}
        customer={customer}
        onCustomerPicked={setCustomer}
        onCustomerCleared={() => setCustomer(null)}
        canExtendCredit={canExtendCredit}
        error={error}
        onHold={holdTicket}
        onClear={askToClear}
      />

      {pickupsOpen && !locked && (
        <PickupsSheet
          currency={currency}
          onClose={() => setPickupsOpen(false)}
          onCollected={() => {
            // Handing an order over takes stock off the shelf, so the tiles,
            // the day's takings and the pickups badge all need to catch up
            refreshCatalog({ silent: true });
            refreshSummary();
            loadPickups();
          }}
        />
      )}

      {packChoice && !locked && (
        <PackChoiceSheet
          pack={packChoice}
          onClose={() => setPackChoice(null)}
          onConfirm={(chosen) => {
            addToTicket(chosen);
            setPackChoice(null);
          }}
        />
      )}

      {openGroup && !locked && (
        <GroupSheet
          group={openGroup}
          members={membersOf.get(openGroup.id) || []}
          quantityOf={ticket.quantityOf}
          lowStockThreshold={lowStockThreshold}
          onAdd={handleAdd}
          onRemove={(member) =>
            ticket.setQuantity(member.id, ticket.quantityOf(member.id) - 1)
          }
          onClose={() => setOpenGroupId(null)}
        />
      )}

      <HeldTicketsSheet
        open={heldOpen && !locked}
        onClose={() => setHeldOpen(false)}
        tickets={held.tickets}
        currency={currency}
        onResume={resumeTicket}
        onDiscard={held.release}
      />

      <RecentSalesSheet
        open={historyOpen && !locked}
        onClose={() => setHistoryOpen(false)}
        currency={currency}
        summary={summary}
        sales={recentSales}
        loading={historyLoading}
        pendingCount={outbox.pendingCount}
        failed={outbox.failed}
        onDiscardFailed={outbox.discardFailed}
      />

      <DoseSheet
        open={dosesOpen && !locked}
        onClose={() => setDosesOpen(false)}
        currency={currency}
        onAddDose={ticket.addProduct}
        quantityOf={ticket.quantityOf}
        // A bottle opened: one fewer on the shelf
        onChanged={() => refreshCatalog({ silent: true })}
      />

      <SupplierSheet
        open={supplierOpen && !locked}
        onClose={() => {
          setSupplierOpen(false);
          setSupplierStart(null);
        }}
        startIn={supplierStart}
        products={catalog.products}
        currency={currency}
        canReceive={canReceive}
        canPay={canPayOut}
        onDone={(message) => {
          setNotice(message);
          // Stock just went up, and a payout came out of the drawer
          refreshCatalog({ silent: true });
          drawer.refresh();
        }}
      />

      <DrawerSheet
        open={drawerOpen && !locked}
        onClose={() => setDrawerOpen(false)}
        drawer={drawer}
        reveal={canPayOut}
        currency={currency}
        onChanged={drawer.refresh}
        onPaySupplier={
          canPayOut && catalog.store.enabled_modules?.suppliers !== false
            ? () => {
                setSupplierStart("payout");
                setSupplierOpen(true);
              }
            : undefined
        }
      />

      {/* Not over the sheet: closing the day ends on its result, read before the next count */}
      {!locked && drawer.mustOpen && !drawerOpen && (
        <DrawerGate currency={currency} onOpened={drawer.refresh} onLock={lock} />
      )}

      {approval && !locked && (
        <OwnerApproval
          clientSaleId={approval.payload.client_sale_id}
          products={approval.products}
          onApproved={(token) => {
            const { payload } = approval;
            setApproval(null);
            submitSale({ ...payload, override_token: token });
          }}
          onCancel={() => setApproval(null)}
        />
      )}

      {tillLock}
      {orderAlert}
    </>
  );
};

export default PosRegister;
